import {
  ActivityMonitor,
  sessionManagement,
  commonActions,
  authenticationContext,
  HttpMethods,
  loggerFactory
} from '@codametrix/ui-common';
import { requestJSON } from '@codametrix/client-api';
import {
  runSessionRenew,
  sendActivityHeartbeat
} from '@codametrix/client-session';
import { getSessionHeartbeatIntervalMs } from '@codametrix/client-runtime';
import store from '../store/store';
import { dismissModal, showModalMessage } from '../actions/ui';
import { shouldRefresh, tokenAvailable } from '../actions/contexts.action';
import { logoutApp } from '../actions/login.action';
import history from '../history';
import { Action } from 'redux';
import { dispatchSessionHeartbeatMiss } from './session-heartbeat-miss';

const { activityMonitor } = sessionManagement;

const logger = loggerFactory('SessionManagement');

const onSessionHeartbeatMiss = () => dispatchSessionHeartbeatMiss(store.dispatch);

const applyToken = (token: string, expiryTimeToLive: number) => {
  store.dispatch(
    tokenAvailable({
      token,
      expiryTimeToLive
    }) as Action
  );
};

/** Re-arm ActivityMonitor without adopting a browser JWT (session-first renew). */
const applyExpiry = (expiryTimeToLive: number) => {
  activityMonitor.expiryTimeout = expiryTimeToLive;
};

const applyRenewTimers = (jwtToken: string | null, expiryTimeToLive: number | null) => {
  if (jwtToken && expiryTimeToLive != null) {
    applyToken(jwtToken, expiryTimeToLive);
    return;
  }
  const ttl = expiryTimeToLive ?? activityMonitor.lastExpiryTimeToLive;
  if (ttl != null) {
    applyExpiry(ttl);
  } else {
    logger.warn('session renew succeeded but no expiryTimeToLive available to re-arm timers');
  }
};

/**
 * Legacy JWT renewal via PUT /contexts/v1 (FRONTEND_SESSION_FIRST off).
 */
const renewContextToken = async (contextId: number) => {
  try {
    const freshContext = await requestJSON<CMxAPI.ContextAPI>(`/contexts/v1`, {
      method: HttpMethods.PUT,
      body: JSON.stringify({ organizationId: contextId })
    });

    applyToken(freshContext.jwtToken, freshContext.expiryTimeToLive);
  } catch (e) {
    // Leave the existing timers running; an unrenewed token expires into the
    // normal TIMEOUT path rather than forcing an extra logout route here.
    logger.error(`context renewal failed`, e);
  }
};

/**
 * Retrieve a new token for a session (legacy PUT or POST /session/renew).
 */
const refreshContext = async () => {
  const contextId = authenticationContext.getContextId();
  if (contextId == null) {
    return;
  }

  store.dispatch(dismissModal() as Action);
  const shouldBroadcastRefresh = activityMonitor.shouldBroadcastRefreshToPeers();
  if (shouldBroadcastRefresh) {
    store.dispatch(shouldRefresh() as Action);
  }

  const renewResult = await runSessionRenew({
    role: shouldBroadcastRefresh ? 'initiator' : 'peer',
    legacyRenew: () => renewContextToken(contextId),
    jitterMs: undefined,
    onToken: ({ token, expiryTimeToLive }) => applyToken(token, expiryTimeToLive),
    onExpiry: applyExpiry,
    onUnauthorized: onSessionHeartbeatMiss,
    onRejectNoPresence: () => {
      logger.warn('session renew rejected: no presence since last renewal');
    },
    onPeerRenewComplete: ({ jwtToken, expiryTimeToLive }) => {
      applyRenewTimers(jwtToken, expiryTimeToLive);
    }
  });

  // Session-first renew may omit TTL briefly; still re-arm from the last known TTL so
  // BEGIN_REFRESH (which cleared timers) does not leave the clock dead.
  if (
    renewResult.outcome === 'renewed' &&
    renewResult.result.jwtToken == null &&
    renewResult.result.expiryTimeToLive == null
  ) {
    applyRenewTimers(null, null);
  }

  const shouldSendHeartbeat =
    shouldBroadcastRefresh &&
    activityMonitor.isActiveSince(
      Date.now() - getSessionHeartbeatIntervalMs()
    );
  await sendActivityHeartbeat({
    contextId,
    shouldSendHeartbeat,
    onSessionMiss: onSessionHeartbeatMiss
  });
};

/**
 * Log the user out of the application
 */
const doLogout = () => {
  history.push('/');
};
/**
 * Handle inactivity from the user
 */
const handleTimeout = () => {
  store.dispatch(dismissModal() as Action);
  store.dispatch((logoutApp({ showModal: false }) as any) as Action);
};

/**
 * Prompt for user to refresh session.
 */

const handleActivityPrompt = () => {
  store.dispatch(
    showModalMessage({
      title: 'Are you still there?',
      buttonText: 'Yes',
      handler: commonActions.UserInterface.REFRESH
    }) as Action
  );
};

activityMonitor.on(
  ActivityMonitor.events.ACTIVITY_PROMPT,
  handleActivityPrompt
);
activityMonitor.on(ActivityMonitor.events.BEGIN_REFRESH, refreshContext);
activityMonitor.on(ActivityMonitor.events.LOGOUT, doLogout);
activityMonitor.on(ActivityMonitor.events.TIMEOUT, handleTimeout);
